import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import useWindowWidth from '../hooks/useWindowWidth';
import DashboardFilterBar from '../components/dashboard/DashboardFilterBar';
import SummaryCards from '../components/dashboard/SummaryCards';
import RevenueVsExpenseChart from '../components/dashboard/RevenueVsExpenseChart';
import ExpensesByCategoryChart from '../components/dashboard/ExpensesByCategoryChart';
import ExpensesByCardChart from '../components/dashboard/ExpensesByCardChart';
import RevenuesByCategoryChart from '../components/dashboard/RevenuesByCategoryChart';
import PaymentStatusChart from '../components/dashboard/PaymentStatusChart';
import KpiCards from '../components/dashboard/KpiCards';
import '../components/dashboard/Dashboard.css';
import { toLocalDateInput } from '@sf/shared';

const getFirstDayOfMonth = () => {
    const now = new Date();
    return toLocalDateInput(new Date(now.getFullYear(), now.getMonth(), 1));
};

const getLastDayOfMonth = () => {
    const now = new Date();
    return toLocalDateInput(new Date(now.getFullYear(), now.getMonth() + 1, 0));
};

// ── Floating Action Button (mobile only) ─────────────────────────────────────
function DashFab() {
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();

    const go = (path) => {
        setOpen(false);
        navigate(path);
    };

    return (
        <div className="dash-fab-wrap">
            <button
                className={`dash-fab-main${open ? ' open' : ''}`}
                onClick={() => setOpen(o => !o)}
                aria-label="Novo lançamento"
            >
                +
            </button>
            {open && (
                <div className="dash-fab-options">
                    <div className="dash-fab-option">
                        <span className="dash-fab-option-label">Receita</span>
                        <button className="dash-fab-option-btn revenue" onClick={() => go('/revenues')}>💰</button>
                    </div>
                    <div className="dash-fab-option">
                        <span className="dash-fab-option-label">Despesa</span>
                        <button className="dash-fab-option-btn expense" onClick={() => go('/expenses')}>💸</button>
                    </div>
                </div>
            )}
        </div>
    );
}

// ── Eye toggle button ─────────────────────────────────────────────────────────
const EyeIcon = ({ open }) => open ? (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
        <circle cx="12" cy="12" r="3"/>
    </svg>
) : (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
        <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
);

function NewDashboardPage() {
    const [filterOptions, setFilterOptions] = useState({ categories: [], banks: [], cards: [] });
    const filterOptionsRef = useRef({ categories: [], banks: [], cards: [] });
    const [filters, setFilters] = useState(null);
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [hideValues, setHideValues] = useState(() => localStorage.getItem('dash_hide_values') !== 'false');
    const width = useWindowWidth();
    const isMobile = width < 768;

    const toggleHideValues = () => {
        setHideValues(prev => {
            const next = !prev;
            localStorage.setItem('dash_hide_values', next);
            return next;
        });
    };

    useEffect(() => {
        Promise.all([
            api.get('/categories'),
            api.get('/banks'),
            api.get('/credit-cards')
        ]).then(([categoriesRes, banksRes, cardsRes]) => {
            const opts = {
                categories: categoriesRes.data,
                banks: banksRes.data,
                cards: cardsRes.data,
            };
            filterOptionsRef.current = opts;
            setFilterOptions(opts);
            setFilters({
                start_date: getFirstDayOfMonth(),
                end_date: getLastDayOfMonth(),
                category_ids: [],
                bank_ids: [],
                card_ids: [],
                statuses: [],
            });
        }).catch(error => {
            console.error("Failed to fetch filter options:", error);
            setLoading(false);
        });
    }, []);

    useEffect(() => {
        if (!filters) return;

        setLoading(true);
        const params = new URLSearchParams({
            start_date: filters.start_date,
            end_date: filters.end_date,
        });

        const opts = filterOptionsRef.current;
        const totalBankOptions = opts.banks.length + 1;
        if (filters.category_ids.length > 0 && filters.category_ids.length < opts.categories.length) {
            filters.category_ids.forEach(id => params.append('category_ids[]', id));
        }
        if (filters.bank_ids.length > 0 && filters.bank_ids.length < totalBankOptions) {
            filters.bank_ids.forEach(id => params.append('bank_ids[]', id));
        }
        if (filters.card_ids.length > 0 && filters.card_ids.length < opts.cards.length) {
            filters.card_ids.forEach(id => params.append('card_ids[]', id));
        }
        filters.statuses.forEach(status => params.append('statuses[]', status));

        api.get(`/dashboard/new?${params.toString()}`)
            .then(response => setDashboardData(response.data))
            .catch(error => console.error("Failed to fetch dashboard data:", error))
            .finally(() => setLoading(false));
    }, [filters]);

    const handleCategoryClick = (category) => {
        const categoryId = category.category_id;
        const currentIds = filters.category_ids;
        const newIds = currentIds.includes(categoryId)
            ? currentIds.filter(id => id !== categoryId)
            : [...currentIds, categoryId];
        setFilters({ ...filters, category_ids: newIds });
    };

    const handleCardClick = (card) => {
        const cardId = card.card_id;
        const currentIds = filters.card_ids;
        const newIds = currentIds.includes(cardId)
            ? currentIds.filter(id => id !== cardId)
            : [...currentIds, cardId];
        setFilters({ ...filters, card_ids: newIds });
    };

    const clearSelectionFilters = () => {
        setFilters({ ...filters, category_ids: [], bank_ids: [], card_ids: [] });
    };

    const hasActiveSelectionFilters = filters && (
        filters.category_ids.length < filterOptions.categories.length ||
        filters.bank_ids.length < filterOptions.banks.length ||
        filters.card_ids.length < filterOptions.cards.length
    );

    if (!filters) {
        return (
            <div className="text-center mt-5">
                <div className="spinner-border" role="status">
                    <span className="visually-hidden">Carregando...</span>
                </div>
            </div>
        );
    }

    // ════════════════════════════════════
    // MOBILE
    // ════════════════════════════════════
    if (isMobile) {
        return (
            <div className="dash-page-m">
                <div className="dash-topbar-m">
                    <button className="dash-hide-btn" onClick={toggleHideValues} title={hideValues ? 'Exibir valores' : 'Ocultar valores'}>
                        <EyeIcon open={!hideValues} />
                    </button>
                </div>
                <SummaryCards data={dashboardData?.summary} hideValues={hideValues} />
                <DashboardFilterBar filters={filters} onFiltersChange={setFilters} filterOptions={filterOptions} />
                <KpiCards data={dashboardData?.kpi} hideValues={hideValues} />

                {loading ? (
                    <div className="dash-loading-m">
                        <div className="spinner-border spinner-border-sm" role="status" />
                        <span>Atualizando dados...</span>
                    </div>
                ) : (
                    <>
                        <RevenueVsExpenseChart data={dashboardData?.revenue_vs_expense} />
                        <ExpensesByCategoryChart
                            data={dashboardData?.expenses_by_category}
                            onCategoryClick={handleCategoryClick}
                        />
                        <RevenuesByCategoryChart data={dashboardData?.revenues_by_category} />
                        <ExpensesByCardChart
                            data={dashboardData?.expenses_by_card}
                            onCardClick={handleCardClick}
                        />
                        <PaymentStatusChart data={dashboardData?.payment_status} hideValues={hideValues} />
                    </>
                )}

                <DashFab />
            </div>
        );
    }

    // ════════════════════════════════════
    // DESKTOP
    // ════════════════════════════════════
    return (
        <div className="container-fluid mt-4">
            <div className="d-flex align-items-center justify-content-between mb-4">
                <h1 className="h2 mb-0">Dashboard Interativo</h1>
                <button className="dash-hide-btn" onClick={toggleHideValues} title={hideValues ? 'Exibir valores' : 'Ocultar valores'}>
                    <EyeIcon open={!hideValues} />
                    <span className="ms-2">{hideValues ? 'Exibir valores' : 'Ocultar valores'}</span>
                </button>
            </div>

            <DashboardFilterBar filters={filters} onFiltersChange={setFilters} filterOptions={filterOptions} />

            {hasActiveSelectionFilters && (
                <div className="mb-3 d-flex align-items-center">
                    <strong className="me-2">Filtros de Seleção Ativos</strong>
                    <button className="btn btn-sm btn-outline-danger ms-2" onClick={clearSelectionFilters}>
                        Limpar Seleções
                    </button>
                </div>
            )}

            {loading ? (
                <div className="text-center">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Carregando...</span>
                    </div>
                </div>
            ) : (
                <>
                    <KpiCards data={dashboardData?.kpi} hideValues={hideValues} />
                    <SummaryCards data={dashboardData?.summary} hideValues={hideValues} />

                    <div className="row mb-4">
                        <div className="col-12">
                            <RevenueVsExpenseChart data={dashboardData?.revenue_vs_expense} />
                        </div>
                    </div>

                    <div className="row mb-4">
                        <div className="col-lg-4">
                            <ExpensesByCategoryChart
                                data={dashboardData?.expenses_by_category}
                                onCategoryClick={handleCategoryClick}
                            />
                        </div>
                        <div className="col-lg-4">
                            <RevenuesByCategoryChart data={dashboardData?.revenues_by_category} />
                        </div>
                        <div className="col-lg-4">
                            <ExpensesByCardChart
                                data={dashboardData?.expenses_by_card}
                                onCardClick={handleCardClick}
                            />
                        </div>
                    </div>

                    <div className="row mb-4">
                        <div className="col-12">
                            <PaymentStatusChart data={dashboardData?.payment_status} hideValues={hideValues} />
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

export default NewDashboardPage;
